import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import { useMutation, useQuery } from "react-query";
import { CloudUpload, NoteAdd, Share } from "@mui/icons-material";
import { useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LabContext, ContextType } from "./index";
import { getLabItemList, getLabReport, sendLabRadiologyReport } from "../services";
import usePopup from "../../../hooks/usePopup";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import usePrint from "../../../hooks/usePrint";
import useUploadLabFile from "../hook/useUploadLabFile";

const WhatsAppIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="#25D366"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LabList = () => {
  const { showSnackbar } = usePopup();
  const { printPdf, loading } = usePrint();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { encounterId } = useParams();
  const { setResultView, setLabData } = useContext(LabContext) as ContextType;
  const [selected, setSelected] = useState({
    index: -1,
    requestId: 0,
  });
  const [sendLoadingIndex, setSendLoadingIndex] = useState(-1);
  const [sharePopup, setSharePopup] = useState<{
    open: boolean;
    labId: number | null;
    index: number;
  }>({ open: false, labId: null, index: -1 });
  if (!setResultView) {
    throw new Error(
      "setResultView is not available. Ensure the component is wrapped in LabProvider."
    );
  }
  const {
    data: labList,
    isLoading,
    refetch,
  } = useQuery(
    ["getLabItemList", encounterId],
    () => getLabItemList(encounterId as string),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: getReport, isLoading: reportLoading } = useMutation(
    getLabReport,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          printPdf(result.response, "new-tab");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );

  const { mutate: sendReport, isLoading: sendReportLoading } = useMutation(
    sendLabRadiologyReport,
    {
      onSuccess: (result) => {
        if (result.responseIndicator === "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
      onSettled: () => {
        setSendLoadingIndex(-1);
        setSharePopup({ open: false, labId: null, index: -1 });
      },
    },
  );

  const clearFile = () => {
    fileInputRef.current!.value = "";
    refetch();
  };
  const { mutate: uploadFile, isLoading: fileUploadLoading } =
    useUploadLabFile(clearFile);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile({
        data: {
          _isEditMode: false,
          _isNew: false,
          _isUpdated: false,
          _isDeleted: false,
          _prevState: false,
          _defaultFieldName: "",
          _userId: 0,
          encounterId: Number(encounterId),
          groupId: 2,
          id: 0,
          labRequestId: selected.requestId,
          isDashBoard: 1,
        },
        file,
      });
    }
  };

  const handleButtonClick = (e: any) => {
    if (fileInputRef.current) {
      setSelected({
        index: Number(e.currentTarget.dataset.index || "-1"),
        requestId: Number(e.currentTarget.dataset.requestid || "-1"),
      });
      fileInputRef.current.click();
    }
  };

  const handlePrintReport = (e: any) => {
    setSelected({
      index: Number(e.currentTarget.dataset.index || "-1"),
      requestId: 0,
    });
    getReport(encounterId as string);
  };

  const handleOpenShare = (labId: number, index: number) => {
    setSharePopup({ open: true, labId, index });
  };

  const handleCloseShare = () => {
    if (!sendReportLoading) {
      setSharePopup({ open: false, labId: null, index: -1 });
    }
  };

  const handleSendReport = (reqId: number, index: number) => {
    setSendLoadingIndex(index);
    sendReport({
      reportType: "LAB",
      encounterId: Number(encounterId),
      reqId,
    });
  };

  return (
    <div className="p-2 mx-2">
      <input
        accept="application/pdf"
        type="file"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <Typography
        sx={(theme) => ({
          color: theme.palette.primary.main,
          fontWeight: "bold",
          marginLeft: "10px",
        })}
      >
        Lab List
      </Typography>
      <div className="border">
        <Table size="small">
          <TableHead
            sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}
          >
            {headings?.map((item: string) => (
              <TableCell
                sx={(theme) => ({ color: theme.palette.background.paper })}
                key={item}
              >
                {item}
              </TableCell>
            ))}
          </TableHead>
          <CustomTableBody
            alert={"No Items"}
            dataLoading={isLoading}
            isData={Boolean(labList?.response?.length)}
            totalSpan={{ col: 4, row: 3 }}
          >
            {labList?.response.map((item, index) => {
              return (
                <TableRow key={`${index}LabList`}>
                  <TableCell width="30%">{item?.name}</TableCell>
                  <TableCell>{item.createdByDate}</TableCell>
                  <TableCell>
                    {item.isPerformed ? (
                      item.isCashed ? (
                        <span className="text-green-600 font-bold">
                          Completed
                        </span>
                      ) : (
                        <span className="text-yellow-600 font-bold">
                          Performed —{" "}
                          <span className="text-red-400 font-bold">
                            ( Not Paid)
                          </span>
                        </span>
                      )
                    ) : (
                      <span className="text-red-400 font-bold">
                        Not Performed
                        <span
                          className={
                            item.isCashed
                              ? "text-green-400 font-bold"
                              : "text-red-400 font-bold"
                          }
                        >
                          ({item.outHouse ? "outhouse" : item.isCashed ? "Paid" : "Not Paid"})
                        </span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Add details">
                      <IconButton
                        onClick={() => {
                          setResultView(item);
                          setLabData((prev) => ({ ...prev, data: [] }));
                        }}
                      >
                        <NoteAdd className="cursor-pointer" color="primary" />
                      </IconButton>
                    </Tooltip>

                    {Boolean(item.isPerformed) && (
                      <>
                        <Tooltip title="Print lab report">
                          <IconButton
                            data-index={index}
                            disabled={reportLoading}
                            color="success"
                            onClick={handlePrintReport}
                          >
                            {index == selected.index &&
                            (reportLoading || loading) ? (
                              <CircularProgress size={20} color="success" />
                            ) : (
                              <LocalPrintshopIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share lab report">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenShare(item.labId, index)}
                          >
                            <Share />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <div className="hidden">
                    <Tooltip title="upload Results">
                      <IconButton
                        data-index={index}
                        data-requestid={item.labId}
                        onClick={handleButtonClick}
                        disabled={fileUploadLoading}
                      >
                        {index == selected.index && fileUploadLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload color="primary" />
                        )}
                      </IconButton>
                    </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </CustomTableBody>
        </Table>
      </div>
      <Dialog
        open={sharePopup.open}
        onClose={handleCloseShare}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Share Report</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <Tooltip title="Share via WhatsApp">
              <IconButton
                disabled={sendReportLoading}
                onClick={() => {
                  if (sharePopup.labId !== null) {
                    handleSendReport(sharePopup.labId, sharePopup.index);
                  }
                }}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {sharePopup.index === sendLoadingIndex && sendReportLoading ? (
                  <CircularProgress size={28} color="success" />
                ) : (
                  <WhatsAppIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabList;
const headings = ["Name", "Date", "Status", "Actions"];
